import React, { useEffect, useMemo, useState, useRef, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import Map, { Marker } from "react-map-gl/maplibre";
import "maplibre-gl/dist/maplibre-gl.css";
import { setBodyBg } from "../../styles/commonHooks";

// ─── Inline global styles (matching EventDetailPage) ─────────────────────────
const GLOBAL_STYLE = `@import url('https://fonts.googleapis.com/css2?family=DM+Sans:ital,opsz,wght@0,9..40,300;0,9..40,400;0,9..40,500;0,9..40,600;1,9..40,300&family=DM+Serif+Display:ital@0;1&display=swap'); @keyframes marker-pulse-ep { 0%, 100% { transform: scale(1); opacity: 0.3; } 50% { transform: scale(1.8); opacity: 0; } } @keyframes rotate { to { transform: rotate(360deg); } } @keyframes shimmer { 0% { background-position: -200% center; } 100% { background-position: 200% center; } } @keyframes spin { to { transform: rotate(360deg); } }`;

function injectEPStyle(css) {
  if (typeof document === "undefined") return;
  if (document.getElementById("ep-global-style")) return;
  const el = document.createElement("style");
  el.id = "ep-global-style";
  el.textContent = css;
  document.head.appendChild(el);
}

/** Persisted map camera after user picks a region (session). */
const MAP_VIEW_STORAGE_KEY = "ep_events_map_view_v1";

function getWorldMapView() {
  return {
    longitude: 12,
    latitude: 26,
    zoom: 1.12,
    bearing: 0,
    pitch: 0,
  };
}

/** Default fallback view: метрополия Франция. */
function getFranceMapView() {
  return {
    longitude: 2.3522,
    latitude: 46.6,
    zoom: 5.2,
    bearing: 0,
    pitch: 0,
  };
}

/**
 * Кластеризует ивенты по сетке ~50 км и возвращает камеру для города
 * с наибольшим числом точек. null — если данных нет.
 *
 * NB: используем plain object как хеш-таблицу, а НЕ `new Map()` —
 * имя `Map` в этом файле занято импортом react-map-gl.
 */
function pickDensestCityView(events) {
  if (!Array.isArray(events) || events.length === 0) return null;
  const today = new Date();
  today.setHours(0, 0, 0, 0);

  const buckets = Object.create(null);
  for (const ev of events) {
    const lat = Number(ev.latitude);
    const lng = Number(ev.longitude);
    if (!Number.isFinite(lat) || !Number.isFinite(lng)) continue;
    const d = new Date(ev.event_date);
    if (Number.isNaN(d.getTime())) continue;
    d.setHours(0, 0, 0, 0);
    if (d < today) continue;

    const key = `${Math.round(lat * 2) / 2}_${Math.round(lng * 2) / 2}`;
    const b = buckets[key] || (buckets[key] = { latSum: 0, lngSum: 0, n: 0 });
    b.latSum += lat;
    b.lngSum += lng;
    b.n += 1;
  }

  let best = null;
  for (const k in buckets) {
    const b = buckets[k];
    if (!best || b.n > best.n) best = b;
  }
  if (!best) return null;

  return {
    latitude: best.latSum / best.n,
    longitude: best.lngSum / best.n,
    zoom: 11,
    bearing: 0,
    pitch: 0,
  };
}

/** API may return time as "HH:mm:ss", string without colons, or a number — never call .split/.slice raw. */
function formatEventTimeShort(t) {
  if (t == null || t === "") return null;
  const s = String(t).trim();
  if (!s) return null;
  return s.length >= 5 ? s.slice(0, 5) : s;
}

function parseEventHourForFilter(t) {
  if (t == null || t === "") return NaN;
  return parseInt(String(t).split(":")[0], 10);
}

function readStoredMapView() {
  if (typeof window === "undefined") return null;
  try {
    const raw = sessionStorage.getItem(MAP_VIEW_STORAGE_KEY);
    if (!raw) return null;
    const p = JSON.parse(raw);
    if (
      typeof p.longitude !== "number" ||
      typeof p.latitude !== "number" ||
      typeof p.zoom !== "number"
    ) {
      return null;
    }
    return { ...getWorldMapView(), ...p, bearing: 0, pitch: 0 };
  } catch {
    return null;
  }
}

let _restCountriesCache = null;
let _restCountriesPromise = null;

function loadRestCountries() {
  if (_restCountriesCache) return Promise.resolve(_restCountriesCache);
  if (_restCountriesPromise) return _restCountriesPromise;
  _restCountriesPromise = fetch(
    "https://restcountries.com/v3.1/all?fields=name,cca2,latlng,translations"
  )
    .then((r) => {
      if (!r.ok) throw new Error("countries");
      return r.json();
    })
    .then((data) => {
      const rows = Array.isArray(data)
        ? data.filter(
            (c) =>
              c &&
              c.cca2 &&
              Array.isArray(c.latlng) &&
              c.latlng.length >= 2 &&
              typeof c.latlng[0] === "number" &&
              typeof c.latlng[1] === "number"
          )
        : [];
      _restCountriesCache = rows;
      _restCountriesPromise = null;
      return rows;
    })
    .catch((e) => {
      _restCountriesPromise = null;
      throw e;
    });
  return _restCountriesPromise;
}

function countryPrimaryLabel(c, lang) {
  if (lang === "fr" && c.translations?.fra?.common) return c.translations.fra.common;
  if (lang === "ru" && c.translations?.rus?.common) return c.translations.rus.common;
  return c.name?.common || String(c.cca2);
}

function countryMatchesQuery(c, q) {
  const needle = (q || "").trim().toLowerCase();
  if (!needle) return true;
  const parts = [
    c.name?.common,
    c.name?.official,
    c.cca2,
    ...(c.translations
      ? Object.values(c.translations).flatMap((t) =>
          [t?.common, t?.official].filter(Boolean)
        )
      : []),
  ];
  const hay = parts.filter(Boolean).join(" ").toLowerCase();
  return hay.includes(needle);
}

function compareCountriesForQuery(a, b, q, lang) {
  const needle = (q || "").trim().toLowerCase();
  const la = countryPrimaryLabel(a, lang).toLowerCase();
  const lb = countryPrimaryLabel(b, lang).toLowerCase();
  const rank = (l) =>
    !needle ? 0 : l.startsWith(needle) ? 0 : l.includes(needle) ? 1 : 2;
  const ra = rank(la);
  const rb = rank(lb);
  if (ra !== rb) return ra - rb;
  return la.localeCompare(lb, lang === "ru" ? "ru" : lang === "fr" ? "fr" : "en");
}

function cityResultLabel(r) {
  const parts = [r.name];
  if (r.admin1) parts.push(r.admin1);
  return parts.join(", ");
}

function zoomForCityResult(r) {
  const code = (r.feature_code || "").toUpperCase();
  if (code === "PPLC" || code === "PPLA" || code === "PPLA2") return 11;
  if (code === "PPL" || code === "PPLX") return 10;
  return 9;
}

const SkeletonCard = () => null;

export default function EventsPage({ lang = "en", theme = "dark", token, isGuruDomain = false }) {
  const navigate = useNavigate();

  // ─── Samsung browser detection & theme derivation — must be before any useEffect ──
  const isSamsungBrowser = () =>
    typeof navigator !== "undefined" && /SamsungBrowser/i.test(navigator.userAgent);
  const activeTheme = isSamsungBrowser() ? "samsung" : theme;
  const isDark = activeTheme === "dark";

  useEffect(() => { injectEPStyle(GLOBAL_STYLE); }, []);
  const [events, setEvents] = useState([]);
  const [myEvents, setMyEvents] = useState([]);
  const [participatingEvents, setParticipatingEvents] = useState([]);
  const [myDrafts, setMyDrafts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadingParticipating, setLoadingParticipating] = useState(false);
  const [, setLoadingDrafts] = useState(false);
  const [isMobile, setIsMobile] = useState(false);
  const [search, setSearch] = useState("");
  const [category, setCategory] = useState("all");
  const [dayFilter, setDayFilter] = useState("all");
  const [timeFilter, setTimeFilter] = useState("all");
  const [activeTab, setActiveTab] = useState("all");
  const eventsContainerRef = useRef(null);
  /** One-time camera for mount only — do not control via viewState+onMove (width/height in viewState caused update loops / blank screen). */
  const mapInitialView = useMemo(() => readStoredMapView() || getFranceMapView(), []);
  const mapRef = useRef(null);
  const mapFlyPendingRef = useRef(null);
  // Стартовое окно выбора региона больше не открываем автоматически — только по кнопке.
  const [mapRegionPickerOpen, setMapRegionPickerOpen] = useState(false);
  // Авто-центрируем на самом густонаселённом городе один раз за сессию,
  // и только если пользователь сам не выбрал регион (нет stored view).
  const didAutoCenterRef = useRef(Boolean(readStoredMapView()));
  const [selectedEvent, setSelectedEvent] = useState(null);

  const [geoPickStep, setGeoPickStep] = useState("country");
  const [countryQuery, setCountryQuery] = useState("");
  const [cityQuery, setCityQuery] = useState("");
  const [countriesData, setCountriesData] = useState([]);
  const [countriesLoadError, setCountriesLoadError] = useState(false);
  const [selectedGeoCountry, setSelectedGeoCountry] = useState(null);
  const [citySuggestions, setCitySuggestions] = useState([]);
  const [cityLoading, setCityLoading] = useState(false);
  const citySearchTimerRef = useRef(null);
  const cityAbortRef = useRef(null);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  useEffect(() => {
    setBodyBg(isDark ? "#020617" : "#FFF3E6");
  }, [isDark]);

  useEffect(() => {
    const mediaQuery = window.matchMedia("(max-width: 768px)");
    const handler = (e) => {
      setIsMobile(e.matches);
    };
    handler(mediaQuery);
    mediaQuery.addEventListener("change", handler);
    return () => mediaQuery.removeEventListener("change", handler);
  }, []);

  useEffect(() => {
    if (!mapRegionPickerOpen) return;
    setGeoPickStep("country");
    setCountryQuery("");
    setCityQuery("");
    setSelectedGeoCountry(null);
    setCitySuggestions([]);
    setCityLoading(false);
    setCountriesLoadError(false);
  }, [mapRegionPickerOpen]);

  useEffect(() => {
    if (!mapRegionPickerOpen || countriesData.length > 0) return;
    loadRestCountries()
      .then((rows) => {
        setCountriesData(rows);
        setCountriesLoadError(false);
      })
      .catch(() => setCountriesLoadError(true));
  }, [mapRegionPickerOpen, countriesData.length]);

  useEffect(() => {
    if (!mapRegionPickerOpen || geoPickStep !== "city" || !selectedGeoCountry) {
      return undefined;
    }
    const q = cityQuery.trim();
    if (q.length < 2) {
      setCitySuggestions([]);
      setCityLoading(false);
      return undefined;
    }

    if (citySearchTimerRef.current) clearTimeout(citySearchTimerRef.current);
    citySearchTimerRef.current = setTimeout(() => {
      cityAbortRef.current?.abort();
      const ac = new AbortController();
      cityAbortRef.current = ac;
      setCityLoading(true);
      const langApi = lang === "fr" ? "fr" : lang === "ru" ? "ru" : "en";
      const url = `https://geocoding-api.open-meteo.com/v1/search?${new URLSearchParams({
        name: q,
        count: "12",
        language: langApi,
        format: "json",
        country: selectedGeoCountry.cca2,
      }).toString()}`;

      fetch(url, { signal: ac.signal })
        .then((r) => {
          if (!r.ok) throw new Error();
          return r.json();
        })
        .then((data) => {
          setCitySuggestions(Array.isArray(data?.results) ? data.results : []);
        })
        .catch((e) => {
          if (e.name !== "AbortError") setCitySuggestions([]);
        })
        .finally(() => setCityLoading(false));
    }, 400);

    return () => {
      if (citySearchTimerRef.current) clearTimeout(citySearchTimerRef.current);
    };
  }, [mapRegionPickerOpen, geoPickStep, selectedGeoCountry, cityQuery, lang]);

  useEffect(() => {
    (async () => {
      try {
        const headers = token ? { Authorization: `Bearer ${token}` } : {};
        const res = await fetch("/api/published_events", { headers });
        if (!res.ok) throw new Error();
        const data = await res.json();
        setEvents(Array.isArray(data) ? data : []);
      } catch (e) {
        console.error("Error loading events:", e);
      } finally {
        setLoading(false);
      }
    })();
  }, [token]);

  // Автоцентр на город с наибольшим числом ивентов.
  // Срабатывает один раз, и только если юзер сам не выбрал регион ранее.
  useEffect(() => {
    if (didAutoCenterRef.current) return;
    if (!events.length) return;
    const target = pickDensestCityView(events);
    if (!target) return;
    didAutoCenterRef.current = true;
    const m = mapRef.current;
    if (m && typeof m.flyTo === "function") {
      m.flyTo({
        center: [target.longitude, target.latitude],
        zoom: target.zoom,
        bearing: 0,
        pitch: 0,
        duration: 700,
        essential: true,
      });
    } else {
      // Карта ещё не смонтирована — отложим до onMapLoad.
      mapFlyPendingRef.current = target;
    }
  }, [events]);

  useEffect(() => {
    if (!token) return;
    (async () => {
      try {
        const res = await fetch("/api/my_published_events", {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (!res.ok) throw new Error();
        const data = await res.json();
        setMyEvents(Array.isArray(data) ? data : []);
      } catch (e) {
        console.error("Error loading my events:", e);
      }
    })();
  }, [token]);

  // Fetch drafts
  useEffect(() => {
    if (!token) return;
    setLoadingDrafts(true);
    (async () => {
      try {
        const res = await fetch("/api/my_drafts", {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (res.ok) {
          const data = await res.json();
          setMyDrafts(Array.isArray(data) ? data : []);
        }
      } catch (e) {
        console.error("Error loading drafts:", e);
      } finally {
        setLoadingDrafts(false);
      }
    })();
  }, [token]);

  useEffect(() => {
    if (!token || activeTab !== "participating") return;
    setLoadingParticipating(true);
    (async () => {
      try {
        const res = await fetch("/api/events/my_registrations", {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (res.ok) {
          const data = await res.json();
          setParticipatingEvents(Array.isArray(data) ? data : []);
        }
      } catch (e) {
        console.error("Error loading participating events:", e);
      } finally {
        setLoadingParticipating(false);
      }
    })();
  }, [token, activeTab]);

  const currentEvents = useMemo(() => {
    let eventsList = [];
    switch (activeTab) {
      case "my_events":
        eventsList = myEvents;
        break;
      case "participating":
        eventsList = participatingEvents;
        break;
      default:
        eventsList = events;
    }
    return eventsList.filter((e) => {
      const matchSearch =
        String(e.title ?? "").toLowerCase().includes(search.toLowerCase()) ||
        String(e.description ?? "").toLowerCase().includes(search.toLowerCase());
      const matchCategory = category === "all" || e.category === category;
      const matchDay =
        dayFilter === "all" ||
        new Date(e.event_date).getDay().toString() === dayFilter;
      let matchTime = true;
      if (timeFilter !== "all" && e.event_time != null && e.event_time !== "") {
        const eventHour = parseEventHourForFilter(e.event_time);
        if (!Number.isFinite(eventHour)) {
          matchTime = false;
        } else if (timeFilter === "morning")
          matchTime = eventHour >= 6 && eventHour < 12;
        else if (timeFilter === "afternoon")
          matchTime = eventHour >= 12 && eventHour < 18;
        else if (timeFilter === "evening")
          matchTime = eventHour >= 18 && eventHour < 24;
        else if (timeFilter === "night")
          matchTime = eventHour >= 0 && eventHour < 6;
      }

      return matchSearch && matchCategory && matchDay && matchTime;
    });
  }, [
    activeTab,
    events,
    myEvents,
    participatingEvents,
    search,
    category,
    dayFilter,
    timeFilter,
  ]);

  // ===== PAST EVENTS (for all tabs) =====
  const pastEvents = useMemo(() => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const source = activeTab === "all" ? events : currentEvents;
    return source
      .filter((e) => {
        const d = new Date(e.event_date);
        d.setHours(0, 0, 0, 0);
        return d < today;
      })
      .sort((a, b) => new Date(b.event_date) - new Date(a.event_date));
  }, [events, currentEvents, activeTab]);

  /** For organizer CTA: allow quick recreate from the most recent past published event. */
  const recreateSourceEventId = useMemo(() => {
    if (!Array.isArray(myEvents) || myEvents.length === 0) return null;
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const pastMine = myEvents
      .filter((e) => {
        const d = new Date(e.event_date);
        if (Number.isNaN(d.getTime())) return false;
        d.setHours(0, 0, 0, 0);
        return d < today;
      })
      .sort((a, b) => new Date(b.event_date) - new Date(a.event_date));
    return pastMine[0]?.id ?? null;
  }, [myEvents]);

  // Filter upcoming from currentEvents (all tabs)
  const upcomingEvents = useMemo(() => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    return currentEvents.filter((e) => {
      const d = new Date(e.event_date);
      d.setHours(0, 0, 0, 0);
      return d >= today;
    });
  }, [currentEvents]);

  const categories = useMemo(
    () => [
      "all",
      ...new Set(currentEvents.map((e) => e.category).filter(Boolean)),
    ],
    [currentEvents]
  );

  const filteredCountries = useMemo(() => {
    if (!countriesData.length) return [];
    const q = countryQuery.trim();
    const list = q ? countriesData.filter((c) => countryMatchesQuery(c, q)) : countriesData;
    return [...list]
      .sort((a, b) => compareCountriesForQuery(a, b, q, lang))
      .slice(0, 80);
  }, [countriesData, countryQuery, lang]);

  // ─── Theme tokens ──────────────────────────────────────────────────────────

  const themeVars = isDark ? {
    bg:           "#020617",
    surface:      "#0d1424",
    card:         "#111827",
    cardHover:    "#1a2540",
    subtleBg:     "rgba(13,20,36,0.85)",
    border:       "rgba(99,102,241,0.12)",
    borderStrong: "rgba(99,102,241,0.28)",
    fg:           "#f1f5f9",
    fgMuted:      "#94a3b8",
    fgSubtle:     "#475569",
    primary:      "#6366f1",
    primaryLight: "#818cf8",
    accent:       "#8b5cf6",
    accentLight:  "#a78bfa",
    success:      "#10b981",
    danger:       "#ef4444",
    tagBg:        "rgba(99,102,241,0.1)",
    tagColor:     "#818cf8",
    inputBg:      "rgba(99,102,241,0.06)",
    overlayBg:    "rgba(2,6,23,0.85)",
    smallText:    "#94a3b8",
    btnGradient:  "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
    btnColor:     "white",
  } : activeTheme === "samsung" ? {
    bg:           "#FFF3E6",
    surface:      "#FFF8F2",
    card:         "#ffffff",
    cardHover:    "#FFF0E0",
    subtleBg:     "rgba(255,248,242,0.97)",
    border:       "rgba(56,25,49,0.12)",
    borderStrong: "rgba(56,25,49,0.28)",
    fg:           "#1a0c14",
    fgMuted:      "#5a3045",
    fgSubtle:     "#8a6070",
    primary:      "#381931",
    primaryLight: "#6b2e58",
    accent:       "#6b2e58",
    accentLight:  "#8a4070",
    success:      "#2a7a50",
    danger:       "#c0392b",
    tagBg:        "rgba(56,25,49,0.08)",
    tagColor:     "#381931",
    inputBg:      "rgba(56,25,49,0.06)",
    overlayBg:    "rgba(0,0,0,0.6)",
    smallText:    "#8a6070",
    btnGradient:  "rgba(56,25,49,0.12)",
    btnColor:     "#381931",
  } : {
    bg:           "#FFF3E6",
    surface:      "#FFF8F2",
    card:         "#ffffff",
    cardHover:    "#FFF0E0",
    subtleBg:     "rgba(255,248,242,0.94)",
    border:       "rgba(56,25,49,0.12)",
    borderStrong: "rgba(56,25,49,0.28)",
    fg:           "#1a0c14",
    fgMuted:      "#5a3045",
    fgSubtle:     "#8a6070",
    primary:      "#381931",
    primaryLight: "#6b2e58",
    accent:       "#6b2e58",
    accentLight:  "#8a4070",
    success:      "#2a7a50",
    danger:       "#c0392b",
    tagBg:        "rgba(56,25,49,0.08)",
    tagColor:     "#381931",
    inputBg:      "rgba(56,25,49,0.05)",
    overlayBg:    "rgba(0,0,0,0.6)",
    smallText:    "#8a6070",
    btnGradient:  "linear-gradient(135deg, #381931 0%, #7d3468 100%)",
    btnColor:     "#FFF3E6",
  };

  const handleMarkerClick = (event) => {
    setSelectedEvent(event);
  };

  const closeModal = () => {
    setSelectedEvent(null);
  };

  const goToEventDetail = () => {
    if (selectedEvent) {
      // ← Обновлено: используем slug если есть, иначе id
      navigate(`/events/${selectedEvent.slug || selectedEvent.id}`);
      closeModal();
    }
  };

  const dayOptions = [
    {
      value: "1",
      label: lang === "ru" ? "Пн" : lang === "fr" ? "Lun" : "Mon",
    },
    {
      value: "2",
      label: lang === "ru" ? "Вт" : lang === "fr" ? "Mar" : "Tue",
    },
    {
      value: "3",
      label: lang === "ru" ? "Ср" : lang === "fr" ? "Mer" : "Wed",
    },
    {
      value: "4",
      label: lang === "ru" ? "Чт" : lang === "fr" ? "Jeu" : "Thu",
    },
    {
      value: "5",
      label: lang === "ru" ? "Пт" : lang === "fr" ? "Ven" : "Fri",
    },
    {
      value: "6",
      label: lang === "ru" ? "Сб" : lang === "fr" ? "Sam" : "Sat",
    },
    {
      value: "0",
      label: lang === "ru" ? "Вс" : lang === "fr" ? "Dim" : "Sun",
    },
  ];

  const timeOptions = [
    {
      value: "all",
      label:
        lang === "ru"
          ? "Всё время"
          : lang === "fr"
          ? "Toute la journée"
          : "All Day",
    },
    {
      value: "morning",
      label:
        lang === "ru"
          ? "Утро (6-12)"
          : lang === "fr"
          ? "Matin (6-12)"
          : "Morning (6-12)",
    },
    {
      value: "afternoon",
      label:
        lang === "ru"
          ? "День (12-18)"
          : lang === "fr"
          ? "Après-midi (12-18)"
          : "Afternoon (12-18)",
    },
    {
      value: "evening",
      label:
        lang === "ru"
          ? "Вечер (18-24)"
          : lang === "fr"
          ? "Soir (18-24)"
          : "Evening (18-24)",
    },
    {
      value: "night",
      label:
        lang === "ru"
          ? "Ночь (0-6)"
          : lang === "fr"
          ? "Nuit (0-6)"
          : "Night (0-6)",
    },
  ];

  const tabs = ["all", "participating"];
  if (myEvents.length > 0 || myDrafts.length > 0) {
    tabs.push("my_events");
  }

  const getTabLabel = (tab) => {
    if (tab === "all")
      return lang === "ru"
        ? "Все события"
        : lang === "fr"
        ? "Tous les événements"
        : "All Events";
    if (tab === "participating")
      return lang === "ru"
        ? "Участвую"
        : lang === "fr"
        ? "Je participe"
        : "Participating";
    if (tab === "my_events") {
      const draftSuffix = myDrafts.length > 0 ? ` · ${myDrafts.length} 📝` : "";
      return lang === "ru"
        ? `Мои события${draftSuffix}`
        : lang === "fr"
        ? `Mes événements${draftSuffix}`
        : `My Events${draftSuffix}`;
    }
    return tab;
  };

  const getGeoModalTitle = () =>
    geoPickStep === "city"
      ? lang === "ru"
        ? "Выберите город"
        : lang === "fr"
        ? "Choisissez une ville"
        : "Choose a city"
      : lang === "ru"
      ? "Выберите страну"
      : lang === "fr"
      ? "Choisissez un pays"
      : "Choose a country";

  const getGeoModalHint = () =>
    geoPickStep === "city"
      ? lang === "ru"
        ? `Страна: ${selectedGeoCountry?.label || "—"}. Введите город — ниже появятся варианты. Карту можно двигать и масштабировать в любой момент.`
        : lang === "fr"
        ? `Pays : ${selectedGeoCountry?.label || "—"}. Saisissez une ville — suggestions ci-dessous. Vous pouvez toujours déplacer et zoomer la carte.`
        : `Country: ${selectedGeoCountry?.label || "—"}. Type a city — suggestions below. You can pan and zoom the map anytime.`
      : lang === "ru"
      ? "Подсказка: выберите страну и город или закройте окно — карта остаётся полностью интерактивной (сдвиг, масштаб)."
      : lang === "fr"
      ? "Astuce : choisissez pays et ville, ou fermez le panneau — la carte reste entièrement interactive (déplacement, zoom)."
      : "Tip: pick a country and city, or close this panel — the map stays fully interactive (pan, zoom).";

  const flyMapToView = (next) => {
    try {
      sessionStorage.setItem(MAP_VIEW_STORAGE_KEY, JSON.stringify(next));
    } catch {
      /* ignore quota / private mode */
    }
    setMapRegionPickerOpen(false);

    const m = mapRef.current;
    if (m && typeof m.flyTo === "function") {
      m.flyTo({
        center: [next.longitude, next.latitude],
        zoom: next.zoom,
        bearing: 0,
        pitch: 0,
        duration: 650,
        essential: true,
      });
    } else {
      mapFlyPendingRef.current = next;
    }
  };

  const onGeoCityPick = (r) => {
    if (typeof r.longitude !== "number" || typeof r.latitude !== "number") return;
    flyMapToView({
      longitude: r.longitude,
      latitude: r.latitude,
      zoom: zoomForCityResult(r),
      bearing: 0,
      pitch: 0,
    });
  };

  const onMapLoad = useCallback(() => {
    const pending = mapFlyPendingRef.current;
    const m = mapRef.current;
    if (pending && m && typeof m.flyTo === "function") {
      m.flyTo({
        center: [pending.longitude, pending.latitude],
        zoom: pending.zoom,
        bearing: 0,
        pitch: 0,
        duration: 0,
        essential: true,
      });
      mapFlyPendingRef.current = null;
    }
  }, []);

  return (
    <>
      {/* ── Background decoration (same approach as EventAdminDashboard) ── */}
      <div aria-hidden="true" style={{
        position: "fixed", inset: 0, zIndex: 0, pointerEvents: "none", overflow: "hidden",
      }}>
        <div style={{
          position: "absolute", top: "-10%", left: "-8%",
          width: "520px", height: "520px", borderRadius: "50%",
          background: isDark
            ? "radial-gradient(circle, rgba(99,102,241,0.18) 0%, transparent 70%)"
            : "radial-gradient(circle, rgba(107,46,88,0.12) 0%, transparent 70%)",
          filter: "blur(50px)",
        }} />
        <div style={{
          position: "absolute", bottom: "-12%", right: "-6%",
          width: "600px", height: "600px", borderRadius: "50%",
          background: isDark
            ? "radial-gradient(circle, rgba(139,92,246,0.14) 0%, transparent 70%)"
            : "radial-gradient(circle, rgba(56,25,49,0.08) 0%, transparent 70%)",
          filter: "blur(60px)",
        }} />
        <div style={{
          position: "absolute", top: "40%", left: "30%",
          width: "700px", height: "400px", borderRadius: "50%",
          background: isDark
            ? "radial-gradient(circle, rgba(124,58,237,0.07) 0%, transparent 70%)"
            : "radial-gradient(circle, rgba(107,46,88,0.05) 0%, transparent 70%)",
          filter: "blur(60px)",
        }} />
        <div style={{
          position: "absolute", inset: 0,
          backgroundImage: isDark
            ? "linear-gradient(rgba(99,102,241,0.04) 1px, transparent 1px), linear-gradient(90deg, rgba(99,102,241,0.04) 1px, transparent 1px)"
            : "linear-gradient(rgba(56,25,49,0.04) 1px, transparent 1px), linear-gradient(90deg, rgba(56,25,49,0.04) 1px, transparent 1px)",
          backgroundSize: "48px 48px",
        }} />
      </div>

      {selectedEvent && (
        <div
          onClick={closeModal}
          style={{
            position: "fixed",
            inset: 0,
            zIndex: 9999,
            background: "rgba(0,0,0,0.6)",
            backdropFilter: "blur(6px)",
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            padding: "16px",
          }}
        >
          <div
            onClick={(e) => e.stopPropagation()}
            style={{
              background: isDark ? themeVars.card : "#ffffff",
              borderRadius: "20px",
              width: "100%",
              maxWidth: "480px",
              maxHeight: "85vh",
              overflowY: "auto",
              overflowX: "hidden",
              boxShadow: isDark
                ? `0 24px 64px rgba(0,0,0,0.65), 0 0 0 1px ${themeVars.border}`
                : `0 24px 64px rgba(0,0,0,0.12), 0 0 0 1px ${themeVars.border}`,
              color: themeVars.fg,
              position: "relative",
            }}
          >
            {selectedEvent.event_cover_url ? (
              <div style={{
                width: "100%",
                height: isMobile ? "180px" : "220px",
                flexShrink: 0,
                position: "relative",
                borderRadius: "20px 20px 0 0",
                overflow: "hidden",
              }}>
                <img
                  src={selectedEvent.event_cover_url}
                  alt={selectedEvent.title || ""}
                  style={{ width: "100%", height: "100%", objectFit: "cover", display: "block" }}
                />
                <div style={{
                  position: "absolute",
                  inset: 0,
                  background: "linear-gradient(to bottom, transparent 50%, rgba(0,0,0,0.45) 100%)",
                }} />
              </div>
            ) : null}

            <button
              onClick={closeModal}
              style={{
                position: "absolute",
                top: "14px",
                right: "14px",
                width: "32px",
                height: "32px",
                borderRadius: "50%",
                border: "none",
                background: isDark ? "rgba(255,255,255,0.08)" : "rgba(0,0,0,0.06)",
                color: themeVars.fg,
                fontSize: "0.85rem",
                cursor: "pointer",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                zIndex: 10,
                lineHeight: 1,
                transition: "background 0.2s",
              }}
              onMouseEnter={(e) => e.currentTarget.style.background = isDark ? "rgba(255,255,255,0.16)" : "rgba(0,0,0,0.12)"}
              onMouseLeave={(e) => e.currentTarget.style.background = isDark ? "rgba(255,255,255,0.08)" : "rgba(0,0,0,0.06)"}
            >
              ✕
            </button>

            <div style={{ padding: isMobile ? "20px" : "28px" }}>
              {selectedEvent.category && (
                <div style={{
                  display: "inline-block",
                  fontSize: "0.72rem",
                  fontWeight: 600,
                  letterSpacing: "0.07em",
                  textTransform: "uppercase",
                  color: themeVars.primary,
                  background: `${themeVars.primary}18`,
                  borderRadius: "6px",
                  padding: "3px 10px",
                  marginBottom: "10px",
                }}>
                  {selectedEvent.category}
                </div>
              )}

              <h2 style={{
                fontSize: isMobile ? "1.25rem" : "1.5rem",
                fontWeight: 700,
                margin: "0 0 6px",
                color: themeVars.fg,
                lineHeight: 1.3,
                wordBreak: "break-word",
                overflowWrap: "break-word",
              }}>
                {selectedEvent.title ||
                  (lang === "ru"
                    ? "Без названия"
                    : lang === "fr"
                    ? "Sans titre"
                    : "Untitled")}
              </h2>

              {selectedEvent.organizer_name && (
                <div style={{
                  fontSize: "0.9rem",
                  color: themeVars.smallText,
                  marginBottom: "18px",
                }}>
                  {selectedEvent.organizer_name}
                </div>
              )}

              <div style={{
                height: "1px",
                background: themeVars.border,
                marginBottom: "16px",
              }} />

              <div style={{
                display: "flex",
                flexWrap: "wrap",
                gap: "8px",
                marginBottom: "18px",
              }}>
                {[
                  {
                    icon: "📍",
                    text: selectedEvent.location || (lang === "ru" ? "Не указано" : lang === "fr" ? "Non spécifié" : "Not specified"),
                  },
                  {
                    icon: "📅",
                    text: new Date(selectedEvent.event_date).toLocaleDateString(
                      lang === "ru" ? "ru-RU" : lang === "fr" ? "fr-FR" : "en-US"
                    ),
                  },
                  ...(() => {
                    const st = formatEventTimeShort(selectedEvent.event_time);
                    return st ? [{ icon: "🕒", text: st }] : [];
                  })(),
                ].map(({ icon, text }, i) => (
                  <div key={i} style={{
                    display: "flex",
                    alignItems: "center",
                    gap: "5px",
                    fontSize: "0.85rem",
                    color: themeVars.smallText,
                    background: themeVars.tagBg,
                    borderRadius: "8px",
                    padding: "5px 10px",
                  }}>
                    <span style={{ fontSize: "0.9rem" }}>{icon}</span>
                    <span>{text}</span>
                  </div>
                ))}
              </div>

              {selectedEvent.description && (
                <p style={{
                  fontSize: "0.9rem",
                  lineHeight: 1.65,
                  color: themeVars.fg,
                  opacity: 0.8,
                  margin: "0 0 20px",
                  wordBreak: "break-word",
                }}>
                  {selectedEvent.description}
                </p>
              )}

              <div style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                gap: "12px",
                paddingTop: "4px",
              }}>
                <div style={{
                  fontSize: isMobile ? "1.15rem" : "1.35rem",
                  fontWeight: 700,
                  color: themeVars.primary,
                }}>
                  {!selectedEvent.ticket_price || selectedEvent.ticket_price === 0
                    ? (lang === "ru" ? "Бесплатно" : lang === "fr" ? "Gratuit" : "Free")
                    : `${selectedEvent.ticket_price} ${selectedEvent.currency || "€"}`}
                </div>

                <button
                  onClick={goToEventDetail}
                  style={{
                    padding: isMobile ? "10px 22px" : "11px 28px",
                    border: "none",
                    borderRadius: "12px",
                    fontWeight: 600,
                    fontSize: "0.95rem",
                    cursor: "pointer",
                    background: themeVars.btnGradient,
                    color: themeVars.btnColor,
                    whiteSpace: "nowrap",
                    flexShrink: 0,
                    transition: "opacity 0.2s, transform 0.2s",
                  }}
                  onMouseEnter={(e) => { e.currentTarget.style.opacity = "0.88"; e.currentTarget.style.transform = "translateY(-1px)"; }}
                  onMouseLeave={(e) => { e.currentTarget.style.opacity = "1"; e.currentTarget.style.transform = "none"; }}
                >
                  {lang === "ru" ? "Подробнее" : lang === "fr" ? "Voir les détails" : "View Details"}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {!isMobile && (
        <div style={{
          position: 'fixed',
          top: 0,
          bottom: 0,
          left: 0,
          right: 0,
          width: '100%',
          background: isDark
            ? 'rgba(13,10,25,0.55)'
            : activeTheme === 'samsung'
            ? 'rgba(255,248,242,0.82)'
            : 'rgba(255,243,230,0.52)',
          backdropFilter: activeTheme === 'samsung' ? 'none' : 'blur(28px)',
          WebkitBackdropFilter: activeTheme === 'samsung' ? 'none' : 'blur(28px)',
          borderLeft: isDark
            ? '1px solid rgba(99,102,241,0.10)'
            : '1px solid rgba(56,25,49,0.10)',
          borderRight: isDark
            ? '1px solid rgba(99,102,241,0.10)'
            : '1px solid rgba(56,25,49,0.10)',
          zIndex: 0,
          pointerEvents: 'none',
        }} />
      )}

      <div
        style={isMobile ? {
          position: "relative",
          zIndex: 1,
          minHeight: "100vh",
          color: themeVars.fg,
          fontFamily: "'DM Sans', sans-serif",
        } : {
          position: "relative",
          zIndex: 1,
          minHeight: "100vh",
          color: themeVars.fg,
          fontFamily: "'DM Sans', sans-serif",
        }}
      >
        <div
          style={{
            width: "100%",
            maxWidth: "100%",
            minHeight: "100vh",
            background: "transparent",
            padding: isMobile ? "30px 16px 110px" : "40px clamp(20px, 3vw, 40px) 140px",
            boxSizing: "border-box",
            margin: "0",
          }}
        >
          <div className="day-slider-container">
            <div className="slider-label">
              {lang === "ru"
                ? "Выберите день недели"
                : lang === "fr"
                ? "Sélectionnez le jour"
                : "Select Day of Week"}
            </div>
            <div className="day-slider">
              <button
                className={`day-option ${dayFilter === "all" ? "active" : ""}`}
                onClick={() => setDayFilter("all")}
              >
                {lang === "ru"
                  ? "Все дни"
                  : lang === "fr"
                  ? "Tous"
                  : "All Days"}
              </button>
              {dayOptions.map((day) => (
                <button
                  key={day.value}
                  className={`day-option ${
                    dayFilter === day.value ? "active" : ""
                  }`}
                  onClick={() => setDayFilter(day.value)}
                >
                  {day.label}
                </button>
              ))}
            </div>
          </div>

          <div className="map-wrap" style={{ position: "relative" }}>
              <button
                type="button"
                aria-haspopup="dialog"
                onClick={() => setMapRegionPickerOpen(true)}
                style={{
                  position: "absolute",
                  top: 8,
                  ...(isMobile ? { left: 8 } : { right: 8 }),
                  zIndex: 15,
                  padding: "8px 12px",
                  borderRadius: 10,
                  border: `1px solid ${themeVars.borderStrong}`,
                  background: themeVars.subtleBg,
                  color: themeVars.fg,
                  fontSize: "0.78rem",
                  fontWeight: 700,
                  cursor: "pointer",
                  boxShadow: isDark
                    ? "0 4px 14px rgba(0,0,0,0.35)"
                    : "0 2px 10px rgba(0,0,0,0.08)",
                  maxWidth: isMobile ? "calc(100% - 120px)" : "none",
                  textAlign: "center",
                  lineHeight: 1.25,
                }}
              >
                {lang === "ru"
                  ? "Сменить регион"
                  : lang === "fr"
                  ? "Changer de région"
                  : "Change region"}
              </button>
              <div
                style={{
                  position: "absolute",
                  inset: 0,
                }}
              >
                <Map
                  ref={mapRef}
                  initialViewState={mapInitialView}
                  onLoad={onMapLoad}
                  mapStyle="https://basemaps.cartocdn.com/gl/dark-matter-gl-style/style.json"
                  style={{
                    width: "100%",
                    height: "100%",
                  }}
                  interactive
                >
                {currentEvents
                  .filter((e) => {
                    const lat = Number(e.latitude);
                    const lng = Number(e.longitude);
                    if (!Number.isFinite(lat) || !Number.isFinite(lng)) return false;
                    const d = new Date(e.event_date);
                    d.setHours(0, 0, 0, 0);
                    const today = new Date();
                    today.setHours(0, 0, 0, 0);
                    return d >= today; // прошедшие ивенты не показываем на карте
                  })
                  .map((e) => (
                    <Marker
                      key={e.id}
                      latitude={Number(e.latitude)}
                      longitude={Number(e.longitude)}
                      anchor="center"
                      onClick={(evt) => {
                        evt.originalEvent.stopPropagation();
                        handleMarkerClick(e);
                      }}
                    >
                      <div
                        className="map-marker-container"
                        style={{
                          position: "relative",
                          width: "44px",
                          height: "44px",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          cursor: "pointer",
                        }}
                      >
                        <div
                          style={{
                            position: "absolute",
                            inset: 0,
                            background: "transparent",
                            zIndex: 2,
                          }}
                        />
                        
                        <div
                          className="map-marker-dot"
                          style={{
                            width: "14px",
                            height: "14px",
                            borderRadius: "50%",
                            background: themeVars.primary,
                            boxShadow: `0 0 16px ${themeVars.primary}cc`,
                            border: "2px solid white",
                            transition: "transform 0.2s ease, box-shadow 0.2s ease",
                            zIndex: 1,
                            pointerEvents: "none",
                          }}
                        />
                        
                        <div
                          style={{
                            position: "absolute",
                            width: "14px",
                            height: "14px",
                            borderRadius: "50%",
                            background: themeVars.primary,
                            opacity: 0.3,
                            pointerEvents: "none",
                          }}
                        />
                      </div>
                    </Marker>
                  ))}
                </Map>
              </div>

              {mapRegionPickerOpen && (
                  <div
                    role="dialog"
                    aria-modal="true"
                    aria-labelledby="ep-map-region-title"
                    style={{
                      position: "absolute",
                      inset: 0,
                      zIndex: 25,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      padding: isMobile ? 12 : 20,
                      pointerEvents: "none",
                    }}
                  >
                    <div
                      className="ep-geo-modal-panel"
                      style={{
                        pointerEvents: "auto",
                        width: "100%",
                        maxWidth: 440,
                        height: "min(86vh, 520px)",
                        maxHeight: "min(86vh, 520px)",
                        borderRadius: 16,
                        border: `1px solid ${themeVars.borderStrong}`,
                        background: themeVars.card,
                        boxShadow: isDark
                          ? `0 24px 64px rgba(0,0,0,0.55), 0 0 0 1px ${themeVars.border}`
                          : `0 24px 64px rgba(0,0,0,0.12), 0 0 0 1px ${themeVars.border}`,
                        padding: isMobile ? 16 : 22,
                        color: themeVars.fg,
                        boxSizing: "border-box",
                        display: "flex",
                        flexDirection: "column",
                        overflow: "hidden",
                      }}
                    >
                      <div
                        style={{
                          display: "flex",
                          alignItems: "flex-start",
                          justifyContent: "space-between",
                          gap: 12,
                          flexShrink: 0,
                        }}
                      >
                        <h3
                          id="ep-map-region-title"
                          style={{
                            margin: 0,
                            flex: 1,
                            minWidth: 0,
                            fontSize: isMobile ? "1.15rem" : "1.25rem",
                            fontWeight: 700,
                            lineHeight: 1.25,
                          }}
                        >
                          {getGeoModalTitle()}
                        </h3>
                        <button
                          type="button"
                          onClick={() => setMapRegionPickerOpen(false)}
                          aria-label={
                            lang === "ru"
                              ? "Закрыть панель"
                              : lang === "fr"
                              ? "Fermer le panneau"
                              : "Close panel"
                          }
                          style={{
                            flexShrink: 0,
                            width: 36,
                            height: 36,
                            borderRadius: 10,
                            border: `1px solid ${themeVars.border}`,
                            background: themeVars.subtleBg,
                            color: themeVars.fg,
                            fontSize: "1.4rem",
                            lineHeight: 1,
                            cursor: "pointer",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            padding: 0,
                          }}
                        >
                          ×
                        </button>
                      </div>
                      <p
                        style={{
                          margin: "8px 0 0",
                          fontSize: "0.9rem",
                          lineHeight: 1.45,
                          color: themeVars.smallText,
                          flexShrink: 0,
                        }}
                      >
                        {getGeoModalHint()}
                      </p>

                      <div
                        style={{
                          flex: 1,
                          minHeight: 0,
                          display: "flex",
                          flexDirection: "column",
                          marginTop: 10,
                          gap: 10,
                        }}
                      >
                      {geoPickStep === "country" && (
                        <>
                          <input
                            type="search"
                            value={countryQuery}
                            onChange={(e) => setCountryQuery(e.target.value)}
                            placeholder={
                              lang === "ru"
                                ? "Например: fra, Germany, Фра…"
                                : lang === "fr"
                                ? "Ex. : fra, allemagne…"
                                : "e.g. fra, Germany, Pol…"
                            }
                            autoComplete="off"
                            autoCapitalize="off"
                            spellCheck={false}
                            style={{
                              flexShrink: 0,
                              width: "100%",
                              boxSizing: "border-box",
                              padding: "12px 14px",
                              borderRadius: 12,
                              border: `1px solid ${themeVars.border}`,
                              background: themeVars.inputBg,
                              color: themeVars.fg,
                              fontSize: "0.95rem",
                              outline: "none",
                            }}
                          />
                          <div
                            className="ep-geo-pick-list"
                            style={{
                              flex: 1,
                              minHeight: 72,
                              overflowY: "auto",
                              overflowX: "hidden",
                              WebkitOverflowScrolling: "touch",
                              touchAction: "pan-y",
                              borderRadius: 12,
                              border: `1px solid ${themeVars.border}`,
                              background: themeVars.subtleBg,
                            }}
                          >
                            {countriesLoadError && (
                              <div style={{ padding: 14 }}>
                                <p style={{ margin: "0 0 10px", fontSize: "0.9rem", color: themeVars.smallText }}>
                                  {lang === "ru"
                                    ? "Не удалось загрузить список стран."
                                    : lang === "fr"
                                    ? "Impossible de charger la liste des pays."
                                    : "Could not load countries."}
                                </p>
                                <button
                                  type="button"
                                  onClick={() => {
                                    setCountriesLoadError(false);
                                    loadRestCountries()
                                      .then((rows) => {
                                        setCountriesData(rows);
                                        setCountriesLoadError(false);
                                      })
                                      .catch(() => setCountriesLoadError(true));
                                  }}
                                  style={{
                                    padding: "8px 14px",
                                    borderRadius: 10,
                                    border: `1px solid ${themeVars.border}`,
                                    background: themeVars.tagBg,
                                    color: themeVars.fg,
                                    fontWeight: 600,
                                    cursor: "pointer",
                                  }}
                                >
                                  {lang === "ru" ? "Повторить" : lang === "fr" ? "Réessayer" : "Retry"}
                                </button>
                              </div>
                            )}
                            {!countriesLoadError && countriesData.length === 0 && (
                              <div
                                style={{
                                  padding: 16,
                                  fontSize: "0.9rem",
                                  color: themeVars.smallText,
                                  textAlign: "center",
                                }}
                              >
                                {lang === "ru" ? "Загрузка…" : lang === "fr" ? "Chargement…" : "Loading…"}
                              </div>
                            )}
                            {!countriesLoadError &&
                              countriesData.length > 0 &&
                              filteredCountries.map((c) => (
                                <button
                                  key={c.cca2}
                                  type="button"
                                  onClick={() => {
                                    setSelectedGeoCountry({
                                      cca2: c.cca2,
                                      label: countryPrimaryLabel(c, lang),
                                      lat: c.latlng[0],
                                      lng: c.latlng[1],
                                    });
                                    setGeoPickStep("city");
                                    setCityQuery("");
                                    setCitySuggestions([]);
                                  }}
                                  style={{
                                    display: "block",
                                    width: "100%",
                                    padding: "10px 14px",
                                    border: "none",
                                    borderBottom: `1px solid ${themeVars.border}`,
                                    background: "transparent",
                                    color: themeVars.fg,
                                    fontSize: "0.9rem",
                                    textAlign: "left",
                                    cursor: "pointer",
                                  }}
                                >
                                  {countryPrimaryLabel(c, lang)}
                                </button>
                              ))}
                            {!countriesLoadError &&
                              countriesData.length > 0 &&
                              filteredCountries.length === 0 && (
                                <div
                                  style={{
                                    padding: 16,
                                    fontSize: "0.9rem",
                                    color: themeVars.smallText,
                                    textAlign: "center",
                                  }}
                                >
                                  {lang === "ru"
                                    ? "Нет совпадений — измените запрос."
                                    : lang === "fr"
                                    ? "Aucun pays correspondant."
                                    : "No matching countries."}
                                </div>
                              )}
                          </div>
                          <button
                            type="button"
                            onClick={() => flyMapToView(getWorldMapView())}
                            style={{
                              flexShrink: 0,
                              width: "100%",
                              padding: "12px 14px",
                              borderRadius: 12,
                              border: `1px solid ${themeVars.border}`,
                              background: themeVars.tagBg,
                              color: themeVars.fg,
                              fontWeight: 600,
                              fontSize: "0.88rem",
                              cursor: "pointer",
                            }}
                          >
                            {lang === "ru"
                              ? "Весь мир"
                              : lang === "fr"
                              ? "Monde entier"
                              : "Whole world"}
                          </button>
                        </>
                      )}

                      {geoPickStep === "city" && selectedGeoCountry && (
                        <>
                          <button
                            type="button"
                            onClick={() => {
                              setGeoPickStep("country");
                              setCityQuery("");
                              setCitySuggestions([]);
                            }}
                            style={{
                              flexShrink: 0,
                              alignSelf: "flex-start",
                              padding: "6px 0",
                              border: "none",
                              background: "none",
                              color: themeVars.primary,
                              fontWeight: 600,
                              fontSize: "0.88rem",
                              cursor: "pointer",
                              textDecoration: "underline",
                            }}
                          >
                            ←{" "}
                            {lang === "ru"
                              ? "Другая страна"
                              : lang === "fr"
                              ? "Autre pays"
                              : "Change country"}
                          </button>
                          <input
                            type="search"
                            value={cityQuery}
                            onChange={(e) => setCityQuery(e.target.value)}
                            placeholder={
                              lang === "ru"
                                ? "Введите город…"
                                : lang === "fr"
                                ? "Saisissez une ville…"
                                : "Type a city…"
                            }
                            autoComplete="off"
                            autoCapitalize="off"
                            spellCheck={false}
                            style={{
                              flexShrink: 0,
                              width: "100%",
                              boxSizing: "border-box",
                              padding: "12px 14px",
                              borderRadius: 12,
                              border: `1px solid ${themeVars.border}`,
                              background: themeVars.inputBg,
                              color: themeVars.fg,
                              fontSize: "0.95rem",
                              outline: "none",
                            }}
                          />
                          <div
                            className="ep-geo-pick-list"
                            style={{
                              flex: 1,
                              minHeight: 72,
                              overflowY: "auto",
                              overflowX: "hidden",
                              WebkitOverflowScrolling: "touch",
                              touchAction: "pan-y",
                              borderRadius: 12,
                              border: `1px solid ${themeVars.border}`,
                              background: themeVars.subtleBg,
                            }}
                          >
                            {cityQuery.trim().length < 2 && (
                              <div
                                style={{
                                  padding: 14,
                                  fontSize: "0.88rem",
                                  color: themeVars.smallText,
                                  textAlign: "center",
                                }}
                              >
                                {lang === "ru"
                                  ? "Минимум 2 символа для поиска."
                                  : lang === "fr"
                                  ? "Au moins 2 caractères."
                                  : "Type at least 2 characters."}
                              </div>
                            )}
                            {cityQuery.trim().length >= 2 && cityLoading && (
                              <div
                                style={{
                                  padding: 14,
                                  fontSize: "0.88rem",
                                  color: themeVars.smallText,
                                  textAlign: "center",
                                }}
                              >
                                {lang === "ru" ? "Поиск…" : lang === "fr" ? "Recherche…" : "Searching…"}
                              </div>
                            )}
                            {cityQuery.trim().length >= 2 &&
                              !cityLoading &&
                              citySuggestions.length === 0 && (
                                <div
                                  style={{
                                    padding: 14,
                                    fontSize: "0.88rem",
                                    color: themeVars.smallText,
                                    textAlign: "center",
                                  }}
                                >
                                  {lang === "ru"
                                    ? "Ничего не найдено."
                                    : lang === "fr"
                                    ? "Aucun résultat."
                                    : "No results."}
                                </div>
                              )}
                            {citySuggestions.map((r) => (
                              <button
                                key={`${r.id}-${r.latitude}-${r.longitude}`}
                                type="button"
                                onClick={() => onGeoCityPick(r)}
                                style={{
                                  display: "block",
                                  width: "100%",
                                  padding: "10px 14px",
                                  border: "none",
                                  borderBottom: `1px solid ${themeVars.border}`,
                                  background: "transparent",
                                  color: themeVars.fg,
                                  fontSize: "0.9rem",
                                  textAlign: "left",
                                  cursor: "pointer",
                                }}
                              >
                                {cityResultLabel(r)}
                                {r.country_code ? (
                                  <span
                                    style={{
                                      display: "block",
                                      fontSize: "0.75rem",
                                      color: themeVars.smallText,
                                      marginTop: 2,
                                    }}
                                  >
                                    {r.country_code}
                                  </span>
                                ) : null}
                              </button>
                            ))}
                          </div>
                        </>
                      )}
                      </div>
                    </div>
                  </div>
              )}

          </div>

          <div className="time-slider-container">
            <div className="slider-label">
              {lang === "ru"
                ? "Выберите время"
                : lang === "fr"
                ? "Sélectionnez l'heure"
                : "Select Time"}
            </div>
            <div className="time-slider">
              {timeOptions.map((time) => (
                <button
                  key={time.value}
                  className={`time-option ${
                    timeFilter === time.value ? "active" : ""
                  }`}
                  onClick={() => setTimeFilter(time.value)}
                >
                  {time.label}
                </button>
              ))}
            </div>
          </div>

          <div className="tabs-container">
            <div className="slider-tabs-large">
              {tabs.map((tab) => (
                <div
                  key={tab}
                  role="button"
                  tabIndex={0}
                  className={`slider-tab-large ${
                    activeTab === tab ? "active" : ""
                  }`}
                  onClick={() => setActiveTab(tab)}
                  onKeyDown={(ev) => {
                    if (ev.key === "Enter" || ev.key === " ") {
                      ev.preventDefault();
                      setActiveTab(tab);
                    }
                  }}
                >
                  <span className="tab-text-large">{getTabLabel(tab)}</span>
                </div>
              ))}
            </div>
          </div>

          <div className="promo-container">
            <div className="promo-text">
              <div className="promo-title">
                {lang === "ru"
                  ? "Хотите создать свой ивент?"
                  : lang === "fr"
                  ? "Vous voulez créer votre propre événement ?"
                  : "Want to create your own event?"}
              </div>
              <div className="promo-subtitle">
                {lang === "ru"
                  ? "Подайте заявку и начните организовывать мероприятия уже сегодня!"
                  : lang === "fr"
                  ? "Soumettez votre demande et commencez à organiser des événements dès aujourd'hui !"
                  : "Submit your application and start organizing events today!"}
              </div>
            </div>
            <div className="promo-actions">
              {recreateSourceEventId ? (
                <button
                  className="promo-btn promo-btn-recreate"
                  onClick={() => navigate(`/events/create?recreate=${recreateSourceEventId}`)}
                >
                  {lang === "ru"
                    ? "Пересоздать ивент"
                    : lang === "fr"
                    ? "Recréer l'événement"
                    : "Recreate Event"}
                </button>
              ) : null}
              <button
                className="promo-btn"
                onClick={() => navigate("/events/create")}
              >
                {lang === "ru"
                  ? "Создать ивент"
                  : lang === "fr"
                  ? "Créer un événement"
                  : "Create Event"}
              </button>
            </div>
          </div>

          <div className="controls">
            <input
              placeholder={
                lang === "ru"
                  ? "Поиск событий..."
                  : lang === "fr"
                  ? "Rechercher des événements..."
                  : "Search events..."
              }
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
            <select
              value={category}
              onChange={(e) => setCategory(e.target.value)}
            >
              <option value="all">
                {lang === "ru"
                  ? "Все категории"
                  : lang === "fr"
                  ? "Toutes les catégories"
                  : "All categories"}
              </option>
              {categories.slice(1).map((c) => (
                <option key={c} value={c}>
                  {c}
                </option>
              ))}
            </select>
          </div>

          <div ref={eventsContainerRef}>
            {/* ===== DRAFTS SECTION (only in my_events tab) ===== */}
            {activeTab === "my_events" && myDrafts.length > 0 && (
              <div style={{ marginBottom: "36px" }}>
                {/* Drafts divider - same style as "Recently Passed" */}
                <div style={{
                  display: "flex", alignItems: "center", gap: "16px",
                  marginBottom: "24px",
                }}>
                  <div style={{
                    flex: 1, height: "1px",
                    background: `linear-gradient(to right, transparent, ${themeVars.border}, transparent)`,
                  }} />
                  <div style={{
                    display: "flex", alignItems: "center", gap: "8px",
                    padding: "6px 20px", borderRadius: "99px",
                    border: `1px solid ${themeVars.border}`,
                    background: themeVars.tagBg,
                  }}>
                    <span style={{ fontSize: "0.75rem", letterSpacing: "0.16em", fontWeight: 700, textTransform: "uppercase", color: themeVars.smallText, opacity: 0.7 }}>
                      {lang === "ru" ? "Черновики" : lang === "fr" ? "Brouillons" : "Drafts"} · {myDrafts.length}
                    </span>
                  </div>
                  <div style={{
                    flex: 1, height: "1px",
                    background: `linear-gradient(to left, transparent, ${themeVars.border}, transparent)`,
                  }} />
                </div>

                <div className="grid">
                  {myDrafts.map((d) => (
                    <div
                      key={`draft-${d.id}`}
                      className="card"
                      onClick={() => navigate(`/events/create?draft=${d.id}`)}
                      style={{ cursor: "pointer" }}
                    >
                      <div className="card-content-wrapper">
                        <div className="photo" style={{ position: "relative" }}>
                          {d.cover_url ? (
                            <img src={d.cover_url} alt={d.title || "Draft"} />
                          ) : (
                            <div style={{
                              color: themeVars.smallText, fontSize: "1.2rem",
                            }}>
                              {(d.title || "Draft").substring(0, 20) + ((d.title || "").length > 20 ? "..." : "")}
                            </div>
                          )}

                          {/* Draft badge — small, top-left */}
                          <div style={{
                            position: "absolute", top: "10px", left: "10px",
                            padding: "3px 10px", borderRadius: "99px",
                            background: "rgba(0,0,0,0.62)",
                            fontSize: "0.7rem", fontWeight: 700, letterSpacing: "0.08em",
                            textTransform: "uppercase", color: "rgba(255,255,255,0.7)",
                          }}>
                            {lang === "ru" ? "Черновик" : lang === "fr" ? "Brouillon" : "Draft"}
                          </div>

                          {/* Completion % — small, top-right */}
                          <div style={{
                            position: "absolute", top: "10px", right: "10px",
                            padding: "3px 10px", borderRadius: "99px",
                            background: d.completion_percent >= 80
                              ? "rgba(16,185,129,0.8)"
                              : d.completion_percent >= 40
                              ? "rgba(251,191,36,0.8)"
                              : "rgba(239,68,68,0.7)",
                            fontSize: "0.7rem", fontWeight: 700, color: "#fff",
                          }}>
                            {d.completion_percent}%
                          </div>
                        </div>

                        <div className="content">
                          <div className="title">
                            {d.title || (lang === "ru" ? "Без названия" : lang === "fr" ? "Sans titre" : "Untitled")}
                          </div>

                          <div className="meta">
                            <div className="meta-line">
                              <span>📅</span>
                              <span>
                                {d.event_date && d.event_date !== "1970-01-01"
                                  ? new Date(d.event_date).toLocaleDateString(
                                      lang === "ru" ? "ru-RU" : lang === "fr" ? "fr-FR" : "en-US"
                                    )
                                  : (lang === "fr" ? "Non spécifié" : "Not specified")}
                              </span>
                            </div>
                            <div className="meta-line">
                              <span>📍</span>
                              <span>
                                {d.location
                                  ? d.location
                                  : (lang === "fr" ? "Non spécifié" : "Not specified")}
                              </span>
                            </div>
                          </div>

                          {/* Progress bar */}
                          <div style={{
                            height: "3px", borderRadius: "99px",
                            background: themeVars.border,
                            overflow: "hidden", marginBottom: "12px",
                          }}>
                            <div style={{
                              height: "100%", borderRadius: "99px",
                              width: `${d.completion_percent}%`,
                              background: d.completion_percent >= 80
                                ? themeVars.success
                                : d.completion_percent >= 40
                                ? "#f59e0b"
                                : themeVars.danger,
                              transition: "width 0.5s ease",
                            }} />
                          </div>

                          <div className="admin-buttons">
                            <button
                              className="admin-btn edit-btn"
                              onClick={(ev) => {
                                ev.stopPropagation();
                                navigate(`/events/create?draft=${d.id}`);
                              }}
                            >
                              {lang === "ru" ? "Продолжить" : lang === "fr" ? "Continuer" : "Continue"}
                            </button>
                            <button
                              className="admin-btn admin-panel-btn"
                              onClick={(ev) => {
                                ev.stopPropagation();
                                if (window.confirm(
                                  lang === "fr"
                                    ? "Supprimer ce brouillon ?"
                                    : "Delete this draft?"
                                )) {
                                  fetch(`/api/events/draft/${d.id}`, {
                                    method: "DELETE",
                                    headers: { Authorization: `Bearer ${token}` },
                                  }).then(() => {
                                    setMyDrafts(prev => prev.filter(x => x.id !== d.id));
                                  });
                                }
                              }}
                            >
                              {lang === "ru" ? "Удалить" : lang === "fr" ? "Supprimer" : "Delete"}
                            </button>
                          </div>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>

                {/* Separator between drafts and published */}
                {upcomingEvents.length > 0 && (
                  <div style={{
                    display: "flex", alignItems: "center", gap: "16px",
                    margin: "28px 0 20px",
                  }}>
                    <div style={{
                      flex: 1, height: "1px",
                      background: `linear-gradient(to right, transparent, ${themeVars.border}, transparent)`,
                    }} />
                    <div style={{
                      padding: "6px 20px", borderRadius: "99px",
                      border: `1px solid ${themeVars.border}`,
                      background: themeVars.tagBg,
                      fontSize: "0.75rem", fontWeight: 700, letterSpacing: "0.16em",
                      textTransform: "uppercase", color: themeVars.smallText, opacity: 0.7,
                    }}>
                      {lang === "ru" ? "Опубликованные" : lang === "fr" ? "Publiés" : "Published"}
                    </div>
                    <div style={{
                      flex: 1, height: "1px",
                      background: `linear-gradient(to left, transparent, ${themeVars.border}, transparent)`,
                    }} />
                  </div>
                )}
              </div>
            )}

            {loading ||
            (activeTab === "participating" && loadingParticipating) ? (
              <div className="grid">
                {Array.from({ length: isMobile ? 4 : 6 }).map((_, i) => (
                  <SkeletonCard key={i} />
                ))}
              </div>
            ) : upcomingEvents.length > 0 ? (
              <div className="grid">
                {upcomingEvents.map((e) => (
                  <div
                    key={e.id}
                    className="card"
                    onClick={() => navigate(`/events/${e.slug || e.id}`)}
                    style={{ cursor: "pointer" }}
                  >
                    <div className="card-content-wrapper">
                      <div className="photo">
                        {e.event_cover_url ? (
                          <img
                            src={e.event_cover_url}
                            alt={e.title || ""}
                          />
                        ) : (
                          <div
                            style={{
                              color: themeVars.smallText,
                              fontSize: "1.2rem",
                            }}
                          >
                            {((e.title || "").substring(0, 20)) +
                              ((e.title || "").length > 20 ? "..." : "")}
                          </div>
                        )}
                      </div>

                      <div className="content">
                        <div className="title">
                          {e.title ||
                            (lang === "ru"
                              ? "Без названия"
                              : lang === "fr"
                              ? "Sans titre"
                              : "Untitled")}
                        </div>

                        <div className="meta">
                          <div className="meta-line">
                            <span>📅</span>
                            <span>
                              {new Date(e.event_date).toLocaleDateString(
                                lang === "ru"
                                  ? "ru-RU"
                                  : lang === "fr"
                                  ? "fr-FR"
                                  : "en-US"
                              )}
                            </span>
                          </div>
                          <div className="meta-line">
                            <span>📍</span>
                            <span>
                              {e.location ||
                                (lang === "ru"
                                  ? "Не указано"
                                  : lang === "fr"
                                  ? "Non spécifié"
                                  : "Not specified")}
                            </span>
                          </div>
                          <div className="meta-line">
                            <span>🕒</span>
                            <span>
                              {formatEventTimeShort(e.event_time) ||
                                (lang === "ru"
                                  ? "Не указано"
                                  : lang === "fr"
                                  ? "Non spécifié"
                                  : "Not specified")}
                            </span>
                          </div>
                          {activeTab === "participating" && (
                            <div
                              className="meta-line"
                              style={{
                                marginTop: "6px",
                                fontWeight: 600,
                                color: e.is_active
                                  ? themeVars.primary
                                  : themeVars.smallText,
                              }}
                            >
                              <span>⭐</span>
                              <span>
                                {e.is_active
                                  ? lang === "ru"
                                    ? "Активное событие"
                                    : lang === "fr"
                                    ? "Événement actif"
                                    : "Active event"
                                  : lang === "ru"
                                  ? "Участвуете"
                                  : lang === "fr"
                                  ? "Vous participez"
                                  : "Participating"}
                              </span>
                            </div>
                          )}
                        </div>

                        {e.description && (
                          <div className="description">
                            {String(e.description).length > 300
                              ? String(e.description).substring(0, 300) + "..."
                              : e.description}
                          </div>
                        )}

                        <div className="price">
                          {e.ticket_price === 0 || !e.ticket_price
                            ? lang === "ru"
                              ? "Бесплатно"
                              : lang === "fr"
                              ? "Gratuit"
                              : "Free"
                            : `${e.ticket_price} ${e.currency || "€"}`}
                        </div>

                        {(activeTab === "my_events" || (activeTab === "participating" && (e.is_co_admin || e.is_mine))) && (
                          <div className="admin-buttons">
                            {activeTab === "my_events" && (
                              <button
                                className="admin-btn edit-btn"
                                onClick={(ev) => {
                                  ev.stopPropagation();
                                  navigate(`/events/${e.id}/edit`);
                                }}
                              >
                                {lang === "ru"
                                  ? "Редактировать"
                                  : lang === "fr"
                                  ? "Modifier"
                                  : "Edit"}
                              </button>
                            )}
                            <button
                              className="admin-btn admin-panel-btn"
                              onClick={(ev) => {
                                ev.stopPropagation();
                                navigate(`/events/${e.slug || e.id}/admin`);
                              }}
                            >
                              {lang === "ru"
                                ? "Админ-панель"
                                : lang === "fr"
                                ? "Panneau d'administration"
                                : "Admin Panel"}
                            </button>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="empty-state">
                {activeTab === "my_events"
                  ? myDrafts.length > 0
                    ? (lang === "ru"
                      ? "Нет опубликованных событий. Завершите черновик выше!"
                      : lang === "fr"
                      ? "Pas encore d'événements publiés. Complétez votre brouillon ci-dessus !"
                      : "No published events yet. Complete your draft above!")
                    : (lang === "ru"
                      ? "У вас пока нет опубликованных событий"
                      : lang === "fr"
                      ? "Vous n'avez pas encore d'événements publiés"
                      : "You don't have any published events yet")
                  : activeTab === "participating"
                  ? lang === "ru"
                    ? "Вы пока не участвуете ни в одном событии"
                    : lang === "fr"
                    ? "Vous ne participez à aucun événement pour le moment"
                    : "You are not participating in any events yet"
                  : lang === "ru"
                  ? "События не найдены"
                  : lang === "fr"
                  ? "Aucun événement trouvé"
                  : "No events found"}
              </div>
            )}
          </div>

          {/* ===== PAST EVENTS SECTION ===== */}
          {pastEvents.length > 0 && (
            <div style={{ marginTop: "48px" }}>
              {/* Divider with label */}
              <div style={{
                display: "flex", alignItems: "center", gap: "16px",
                marginBottom: "32px",
              }}>
                <div style={{
                  flex: 1, height: "1px",
                  background: `linear-gradient(to right, transparent, ${themeVars.border}, transparent)`,
                }} />
                <div style={{
                  display: "flex", alignItems: "center", gap: "8px",
                  padding: "6px 20px", borderRadius: "99px",
                  border: `1px solid ${themeVars.border}`,
                  background: themeVars.tagBg,
                }}>
                  <span style={{ fontSize: "0.75rem", letterSpacing: "0.16em", fontWeight: 700, textTransform: "uppercase", color: themeVars.smallText, opacity: 0.7 }}>
                    {lang === "ru" ? "Прошедшие" : lang === "fr" ? "Récents" : "Recently Passed"}
                  </span>
                </div>
                <div style={{
                  flex: 1, height: "1px",
                  background: `linear-gradient(to left, transparent, ${themeVars.border}, transparent)`,
                }} />
              </div>

              {/* Past events grid with fog effect */}
              <div style={{ position: "relative" }}>
                <div className="grid">
                  {pastEvents.map((e) => (
                    <div key={e.id} style={{ overflow: "hidden" }}>
                    <div
                      className="card past-card"
                      onClick={() => navigate(`/events/${e.slug || e.id}`)}
                      style={{ cursor: "pointer", opacity: 0.55, filter: "grayscale(0.35)" }}
                    >
                      <div className="card-content-wrapper">
                        <div className="photo">
                          {e.event_cover_url ? (
                            <img src={e.event_cover_url} alt={e.title || ""} />
                          ) : (
                            <div style={{ color: themeVars.smallText, fontSize: "1.2rem" }}>
                              {((e.title || "").substring(0, 20)) +
                                ((e.title || "").length > 20 ? "..." : "")}
                            </div>
                          )}
                          {/* Past badge */}
                          <div style={{
                            position: "absolute", top: "10px", left: "10px",
                            padding: "3px 10px", borderRadius: "99px",
                            background: "rgba(0,0,0,0.62)",
                            fontSize: "0.7rem", fontWeight: 700, letterSpacing: "0.08em",
                            textTransform: "uppercase", color: "rgba(255,255,255,0.7)",
                          }}>
                            {lang === "ru" ? "Прошло" : lang === "fr" ? "Passé" : "Past"}
                          </div>
                        </div>
                        <div className="content">
                          <div className="title">
                            {e.title ||
                              (lang === "ru"
                                ? "Без названия"
                                : lang === "fr"
                                ? "Sans titre"
                                : "Untitled")}
                          </div>
                          <div className="meta">
                            <div className="meta-line">
                              <span>📅</span>
                              <span>{new Date(e.event_date).toLocaleDateString(lang === "ru" ? "ru-RU" : lang === "fr" ? "fr-FR" : "en-US")}</span>
                            </div>
                            <div className="meta-line">
                              <span>📍</span>
                              <span>{e.location || (lang === "ru" ? "Не указано" : lang === "fr" ? "Non spécifié" : "Not specified")}</span>
                            </div>
                          </div>
                          {e.description && (
                            <div className="description">
                              {String(e.description).length > 200
                                ? String(e.description).substring(0, 200) + "..."
                                : e.description}
                            </div>
                          )}
                          <div className="price" style={{ opacity: 0.7 }}>
                            {e.ticket_price === 0 || !e.ticket_price ? (lang === "ru" ? "Бесплатно" : lang === "fr" ? "Gratuit" : "Free") : `${e.ticket_price} ${e.currency || "€"}`}
                          </div>
                        </div>
                      </div>
                    </div>
                    </div>
                  ))}
                </div>
                {/* Fog overlay at top */}
                <div style={{
                  position: "absolute", top: 0, left: 0, right: 0, height: "80px",
                  background: `linear-gradient(to bottom, ${themeVars.bg}, transparent)`,
                  pointerEvents: "none",
                }} />
              </div>
            </div>
          )}

          {currentEvents.length > 0 && (
            <div className="promo-bottom">
              <div className="promo-text">
                <div className="promo-title">
                  {lang === "ru"
                    ? "Хотите создать свой ивент?"
                    : lang === "fr"
                    ? "Vous voulez créer votre propre événement ?"
                    : "Want to create your own event?"}
                </div>
                <div className="promo-subtitle">
                  {lang === "ru"
                    ? "Подайте заявку и начните организовывать мероприятия уже сегодня!"
                    : lang === "fr"
                    ? "Soumettez votre demande et commencez à organiser des événements dès aujourd'hui !"
                    : "Submit your application and start organizing events today!"}
                </div>
              </div>
              <div className="promo-actions">
                {recreateSourceEventId ? (
                  <button
                    className="glass-btn glass-btn-recreate"
                    onClick={() => navigate(`/events/create?recreate=${recreateSourceEventId}`)}
                  >
                    {lang === "ru"
                      ? "Пересоздать ивент"
                      : lang === "fr"
                      ? "Recréer l'événement"
                      : "Recreate Event"}
                  </button>
                ) : null}
                <button
                  className="glass-btn"
                  onClick={() => navigate("/events/create")}
                >
                  {lang === "ru"
                    ? "Создать ивент"
                    : lang === "fr"
                    ? "Créer un événement"
                    : "Create Event"}
                </button>
              </div>
            </div>
          )}
        </div>

        <style jsx global>{`
          .day-slider-container {
            margin-bottom: 20px;
            width: 100%;
          }
          .slider-label {
            font-size: ${isMobile ? "0.85rem" : "0.95rem"};
            font-weight: 600;
            color: ${themeVars.smallText};
            margin-bottom: 10px;
            text-align: ${isMobile ? "center" : "left"};
          }
          .day-slider {
            display: flex;
            width: 100%;
            gap: 0;
            border-radius: ${isMobile ? "8px" : "10px"};
            overflow: hidden;
            border: 1px solid ${themeVars.border};
            background: ${themeVars.inputBg};
            height: ${isMobile ? "38px" : "42px"};
          }
          .day-option {
            flex: 1;
            padding: ${isMobile ? "10px 4px" : "12px 6px"};
            border: none;
            border-right: 1px solid ${themeVars.border};
            background: transparent;
            color: ${themeVars.smallText};
            font-size: ${isMobile ? "0.75rem" : "0.85rem"};
            font-weight: 600;
            cursor: pointer;
            transition: all 0.3s ease;
            white-space: nowrap;
          }
          .day-option:last-child {
            border-right: none;
          }
          .day-option:hover {
            background: ${themeVars.cardHover};
            color: ${themeVars.fg};
          }
          .day-option.active {
            background: ${themeVars.btnGradient};
            color: ${themeVars.btnColor};
            box-shadow: inset 0 2px 8px rgba(0, 0, 0, 0.2);
          }
          .time-slider-container {
            margin-bottom: 24px;
            width: 100%;
          }
          .time-slider {
            display: flex;
            width: 100%;
            gap: 0;
            border-radius: ${isMobile ? "8px" : "10px"};
            overflow: hidden;
            border: 1px solid ${themeVars.border};
            background: ${themeVars.inputBg};
            height: ${isMobile ? "38px" : "42px"};
          }
          .time-option {
            flex: 1;
            padding: ${isMobile ? "10px 6px" : "12px 8px"};
            border: none;
            border-right: 1px solid ${themeVars.border};
            background: transparent;
            color: ${themeVars.smallText};
            font-size: ${isMobile ? "0.75rem" : "0.85rem"};
            font-weight: 600;
            cursor: pointer;
            transition: all 0.3s ease;
            white-space: nowrap;
          }
          .time-option:last-child {
            border-right: none;
          }
          .time-option:hover {
            background: ${themeVars.cardHover};
            color: ${themeVars.fg};
          }
          .time-option.active {
            background: ${themeVars.btnGradient};
            color: ${themeVars.btnColor};
            box-shadow: inset 0 2px 8px rgba(0, 0, 0, 0.2);
          }
          .controls {
            display: grid;
            grid-template-columns: ${isMobile ? "1fr" : "2fr 1fr"};
            gap: ${isMobile ? "12px" : "14px"};
            margin-bottom: ${isMobile ? "24px" : "32px"};
          }
          input,
          select {
            font-family: 'DM Sans', sans-serif;
            background: ${themeVars.inputBg};
            border: 1px solid ${themeVars.border};
            color: ${themeVars.fg};
            padding: ${isMobile ? "11px 14px" : "13px 16px"};
            border-radius: ${isMobile ? "12px" : "14px"};
            font-size: ${isMobile ? "0.95rem" : "1rem"};
            outline: none;
            transition: border-color 0.2s;
          }
          select option {
            background: ${themeVars.card};
            color: ${themeVars.fg};
          }
          .map-wrap {
            height: ${isMobile ? "400px" : "600px"};
            border-radius: ${isMobile ? "12px" : "16px"};
            overflow: hidden;
            border: 1px solid ${themeVars.border};
            margin-bottom: ${isMobile ? "20px" : "24px"};
            background: rgba(0, 0, 0, 0.15);
            position: relative;
          }
          .ep-geo-pick-list {
            scrollbar-width: none;
            -ms-overflow-style: none;
          }
          .ep-geo-pick-list::-webkit-scrollbar {
            display: none;
          }
          .map-marker-container {
            position: relative;
            width: 44px;
            height: 44px;
            display: flex;
            align-items: center;
            justify-content: center;
            cursor: pointer;
          }
          .map-marker-dot {
            width: 14px;
            height: 14px;
            border-radius: 50%;
            background: ${themeVars.primary};
            box-shadow: 0 0 16px ${themeVars.primary}cc;
            border: 2px solid white;
            transition: transform 0.2s ease, box-shadow 0.2s ease;
            pointer-events: none;
            transform-origin: center;
          }
          .map-marker-container:hover .map-marker-dot {
            transform: scale(1.5);
            box-shadow: 0 0 24px rgba(99, 102, 241, 0.8);
          }
          @keyframes marker-pulse {
            0%, 100% {
              transform: scale(1);
              opacity: 0.3;
            }
            50% {
              transform: scale(1.8);
              opacity: 0;
            }
          }
          .map-wrap .maplibregl-canvas {
            cursor: pointer;
          }
          .map-wrap .maplibregl-ctrl-attrib {
            display: none;
          }
          .map-toggle-btn {
            position: relative;
            background: ${themeVars.btnGradient};
            color: ${themeVars.btnColor};
            border: none;
            padding: 16px 32px;
            border-radius: 16px;
            font-weight: 700;
            font-size: 1.05rem;
            cursor: pointer;
            box-shadow: 0 4px 20px ${isDark ? "rgba(99,102,241,0.38)" : "rgba(56,25,49,0.35)"};
            transition: all 0.3s ease;
            overflow: hidden;
            display: flex;
            align-items: center;
            gap: 10px;
            z-index: 1;
          }
          .map-toggle-btn::before {
            content: "";
            position: absolute;
            top: -50%;
            left: -50%;
            width: 200%;
            height: 200%;
            background: conic-gradient(
              from 0deg,
              transparent 0deg,
              rgba(255, 255, 255, 0.3) 60deg,
              transparent 120deg,
              rgba(255, 255, 255, 0.3) 180deg,
              transparent 240deg,
              rgba(255, 255, 255, 0.3) 300deg,
              transparent 360deg
            );
            animation: rotate 4s linear infinite;
            opacity: 0;
            transition: opacity 0.4s ease;
          }
          .map-toggle-btn:hover::before {
            opacity: 1;
          }
          .map-toggle-btn::after {
            content: "";
            position: absolute;
            inset: 2px;
            background: ${themeVars.btnGradient};
            border-radius: 14px;
            z-index: -1;
          }
          .btn-glow {
            position: absolute;
            top: 50%;
            left: 50%;
            transform: translate(-50%, -50%);
            width: 120%;
            height: 120%;
            background: radial-gradient(
              circle,
              rgba(99, 102, 241, 0.6) 0%,
              transparent 70%
            );
            opacity: 0;
            transition: opacity 0.4s ease;
            pointer-events: none;
          }
          .map-toggle-btn:hover .btn-glow {
            opacity: 1;
            animation: pulse 2s ease-in-out infinite;
          }
          .btn-particles {
            position: absolute;
            top: 0;
            left: 0;
            width: 100%;
            height: 100%;
            background-image: radial-gradient(circle at 20% 30%, rgba(255, 255, 255, 0.4) 1px, transparent 1px),
              radial-gradient(circle at 80% 70%, rgba(255, 255, 255, 0.3) 1px, transparent 1px),
              radial-gradient(circle at 40% 80%, rgba(255, 255, 255, 0.35) 1px, transparent 1px);
            background-size: 100px 100px;
            opacity: 0;
            transition: opacity 0.4s ease;
            animation: particles 3s linear infinite;
          }
          .map-toggle-btn:hover .btn-particles {
            opacity: 1;
          }
          .btn-text {
            position: relative;
            z-index: 2;
            text-shadow: 0 2px 8px rgba(0, 0, 0, 0.3);
          }
          .btn-shine {
            position: absolute;
            top: 0;
            left: -100%;
            width: 100%;
            height: 100%;
            background: linear-gradient(
              90deg,
              transparent,
              rgba(255, 255, 255, 0.4),
              transparent
            );
            transition: left 0.6s ease;
          }
          .map-toggle-btn:hover .btn-shine {
            left: 100%;
          }
          .map-toggle-btn:hover {
            transform: translateY(-2px) scale(1.02);
            box-shadow: 0 8px 28px ${isDark ? "rgba(99,102,241,0.5)" : "rgba(56,25,49,0.45)"};
          }
          .map-toggle-btn:active {
            transform: translateY(1px) scale(0.98);
          }
          @keyframes rotate {
            from {
              transform: rotate(0deg);
            }
            to {
              transform: rotate(360deg);
            }
          }
          @keyframes pulse {
            0%,
            100% {
              transform: translate(-50%, -50%) scale(1);
              opacity: 0.5;
            }
            50% {
              transform: translate(-50%, -50%) scale(1.1);
              opacity: 0.8;
            }
          }
          @keyframes particles {
            0% {
              background-position: 0 0, 0 0, 0 0;
            }
            100% {
              background-position: 100px 100px, 100px 100px, 100px 100px;
            }
          }
          .map-close-btn {
            position: absolute;
            top: 10px;
            right: 10px;
            z-index: 20;
            background: rgba(0, 0, 0, 0.65);
            color: white;
            border: 1px solid ${themeVars.border};
            padding: 8px 16px;
            border-radius: 10px;
            font-size: 0.85rem;
            font-weight: 600;
            cursor: pointer;
            transition: all 0.2s ease;
          }
          .map-close-btn:hover {
            background: ${themeVars.primary};
            transform: scale(1.04);
          }
          .tabs-container {
            display: flex;
            flex-direction: ${isMobile ? "column" : "row"};
            justify-content: ${isMobile ? "center" : "space-between"};
            align-items: center;
            gap: ${isMobile ? "16px" : "0"};
            margin-bottom: ${isMobile ? "28px" : "40px"};
          }
          .slider-tabs-large {
            display: flex;
            flex-wrap: wrap;
            justify-content: center;
            gap: 8px;
            background: ${themeVars.inputBg};
            border: 1px solid ${themeVars.border};
            border-radius: 12px;
            padding: 5px;
            min-height: ${isMobile ? "42px" : "48px"};
            width: ${isMobile ? "100%" : "auto"};
            position: relative;
          }
          .slider-tab-large {
            padding: ${isMobile ? "6px 12px" : "8px 18px"};
            border: none;
            border-radius: 10px;
            background: transparent;
            color: ${themeVars.smallText};
            font-size: ${isMobile ? "0.8rem" : "0.9rem"};
            font-weight: 700;
            cursor: pointer;
            transition: all 0.2s ease;
            white-space: nowrap;
            text-align: center;
            display: flex;
            align-items: center;
            justify-content: center;
            position: relative;
            overflow: visible;
            min-width: ${isMobile ? "auto" : "100px"};
            flex: ${isMobile ? "0 0 auto" : "none"};
          }
          .slider-tab-large:hover:not(.active) {
            background: ${themeVars.cardHover};
            color: ${themeVars.fg};
          }
          .slider-tab-large.active {
            background: ${themeVars.btnGradient};
            color: ${themeVars.btnColor};
            box-shadow: 0 2px 12px rgba(56,25,49,0.22);
          }
          .tab-text-large {
            position: relative;
            z-index: 3;
          }
          .grid {
            display: grid;
            grid-template-columns: repeat(auto-fill, minmax(${isMobile ? "100%" : "420px"}, 1fr));
            gap: ${isMobile ? "16px" : "24px"};
          }
          .card {
            background: ${themeVars.card};
            border: 1px solid ${themeVars.border};
            overflow: hidden;
            cursor: pointer;
            position: relative;
            transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
            transform-origin: center;
            display: flex;
            flex-direction: column;
          }
          .card::before {
            content: "";
            position: absolute;
            top: 0;
            left: 0;
            right: 0;
            bottom: 0;
            background: linear-gradient(135deg, ${themeVars.primary}22, ${themeVars.accent}22);
            opacity: 0;
            transition: opacity 0.3s ease;
            z-index: 0;
          }
          .card:hover {
            transform: translateY(-4px) scale(1.02);
            border-color: ${themeVars.borderStrong};
            box-shadow: 0 12px 32px ${isDark ? "rgba(99,102,241,0.22)" : "rgba(56,25,49,0.18)"}, 0 0 0 1px ${themeVars.borderStrong};
          }
          .card:hover::before {
            opacity: 1;
          }
          .card:active {
            transform: translateY(2px) scale(0.98);
            box-shadow: 0 4px 12px ${isDark ? "rgba(99,102,241,0.15)" : "rgba(56,25,49,0.12)"};
          }
          .past-card:hover {
            transform: none;
            box-shadow: none;
          }
          .past-card:active {
            transform: none;
          }
          .past-card {
            height: auto;
          }
          .card-content-wrapper {
            position: relative;
            z-index: 1;
            height: 100%;
            display: flex;
            flex-direction: column;
            overflow: hidden;
          }
          .photo {
            aspect-ratio: 16 / 9;
            width: 100%;
            background: rgba(255, 255, 255, 0.08);
            display: flex;
            align-items: center;
            justify-content: center;
            overflow: hidden;
            position: relative;
            flex-shrink: 0;
          }
          .photo::after {
            content: "";
            position: absolute;
            top: 0;
            left: 0;
            right: 0;
            bottom: 0;
            background: linear-gradient(to top, rgba(0, 0, 0, 0.4), transparent 70%);
            opacity: 0;
            transition: opacity 0.3s ease;
          }
          .card:hover .photo::after {
            opacity: 1;
          }
          .photo img {
            width: 100%;
            height: 100%;
            object-fit: cover;
            transition: transform 0.5s ease;
          }
          .card:hover .photo img {
            transform: scale(1.08);
          }
          .content {
            padding: ${isMobile ? "14px" : "18px"};
            flex: 1;
            min-height: 0;
            overflow: hidden;
            display: flex;
            flex-direction: column;
          }
          .title {
            font-family: 'DM Serif Display', serif;
            font-size: ${isMobile ? "1.2rem" : "1.35rem"};
            font-weight: 400;
            letter-spacing: -0.01em;
            margin-bottom: 10px;
            white-space: nowrap;
            overflow: hidden;
            text-overflow: ellipsis;
            transition: color 0.3s ease;
            flex-shrink: 0;
          }
          .card:hover .title {
            color: ${themeVars.primary};
          }
          .meta {
            font-size: ${isMobile ? "0.88rem" : "0.92rem"};
            color: ${themeVars.smallText};
            margin-bottom: ${isMobile ? "10px" : "12px"};
            display: flex;
            flex-direction: column;
            gap: ${isMobile ? "5px" : "6px"};
            flex-shrink: 0;
          }
          .meta-line {
            display: flex;
            align-items: center;
            gap: 6px;
            overflow: hidden;
          }
          .meta-line span:first-child {
            min-width: 20px;
            flex-shrink: 0;
            font-weight: 600;
          }
          .meta-line span:last-child {
            white-space: nowrap;
            overflow: hidden;
            text-overflow: ellipsis;
          }
          .description {
            font-size: ${isMobile ? "0.9rem" : "0.95rem"};
            color: ${themeVars.fg};
            opacity: 0.85;
            margin-bottom: ${isMobile ? "10px" : "12px"};
            display: -webkit-box;
            -webkit-line-clamp: ${isMobile ? 3 : 4};
            -webkit-box-orient: vertical;
            overflow: hidden;
            text-overflow: ellipsis;
            line-height: 1.5;
            flex-shrink: 1;
            min-height: 0;
          }
          .price {
            font-family: 'DM Serif Display', serif;
            font-size: ${isMobile ? "1.1rem" : "1.25rem"};
            font-weight: 400;
            color: ${themeVars.primary};
            margin-top: auto;
            padding-top: 8px;
            flex-shrink: 0;
          }
          .admin-buttons {
            display: flex;
            gap: ${isMobile ? "8px" : "10px"};
            margin-top: 8px;
            flex-shrink: 0;
          }
          .admin-btn {
            flex: 1;
            padding: ${isMobile ? "8px" : "10px"};
            border: none;
            border-radius: ${isMobile ? "6px" : "8px"};
            font-size: ${isMobile ? "0.85rem" : "0.95rem"};
            cursor: pointer;
            font-weight: 600;
            transition: all 0.2s ease;
            position: relative;
            overflow: hidden;
          }
          .admin-btn::before {
            content: "";
            position: absolute;
            top: 0;
            left: -100%;
            width: 100%;
            height: 100%;
            background: rgba(255, 255, 255, 0.2);
            transition: left 0.3s ease;
          }
          .admin-btn:hover::before {
            left: 100%;
          }
          .admin-btn:hover {
            transform: translateY(-2px);
            box-shadow: 0 6px 16px rgba(0, 0, 0, 0.2);
          }
          .admin-btn:active {
            transform: translateY(1px);
            box-shadow: 0 2px 8px rgba(0, 0, 0, 0.15);
          }
          .edit-btn {
            background: ${themeVars.btnGradient};
            color: ${themeVars.btnColor};
          }
          .admin-btn.admin-panel-btn {
            background: ${themeVars.btnGradient};
            color: ${themeVars.btnColor};
          }
          .empty-state {
            text-align: center;
            padding: 100px 20px;
            color: ${themeVars.fgMuted};
            font-size: 1.1rem;
            opacity: 0.75;
          }
          .promo-container {
            background: linear-gradient(135deg, ${themeVars.primary}0d, ${themeVars.accent}0d);
            border: 1px solid ${themeVars.border};
            border-radius: ${isMobile ? "12px" : "16px"};
            padding: ${isMobile ? "20px" : "32px"};
            margin: ${isMobile ? "20px 0" : "32px 0"};
            display: flex;
            flex-direction: ${isMobile ? "column" : "row"};
            align-items: ${isMobile ? "center" : "center"};
            justify-content: ${isMobile ? "center" : "space-between"};
            gap: ${isMobile ? "16px" : "0"};
            transition: all 0.2s ease;
          }
          .promo-container:hover {
            box-shadow: 0 6px 20px ${themeVars.primary}20;
          }
          .promo-text {
            flex: 1;
            text-align: ${isMobile ? "center" : "left"};
          }
          .promo-actions {
            display: flex;
            align-items: center;
            justify-content: center;
            flex-wrap: wrap;
            gap: ${isMobile ? "10px" : "12px"};
          }
          .promo-title {
            font-size: ${isMobile ? "1.3rem" : "1.6rem"};
            font-weight: 700;
            margin-bottom: ${isMobile ? "6px" : "8px"};
            color: ${themeVars.fg};
            font-family: 'DM Sans', sans-serif;
            letter-spacing: -0.01em;
          }
          .promo-subtitle {
            font-size: ${isMobile ? "0.85rem" : "1.05rem"};
            color: ${themeVars.smallText};
            opacity: 0.9;
            line-height: 1.5;
            font-family: 'DM Sans', sans-serif;
          }
          .promo-btn {
            background: ${themeVars.btnGradient};
            border: none;
            border-radius: 12px;
            padding: ${isMobile ? "10px 24px" : "14px 32px"};
            color: ${themeVars.btnColor};
            font-size: ${isMobile ? "0.95rem" : "1.1rem"};
            font-weight: 600;
            font-family: 'DM Sans', sans-serif;
            cursor: pointer;
            transition: all 0.3s ease;
            box-shadow: 0 6px 20px ${isDark ? "rgba(102,126,234,0.35)" : "rgba(56,25,49,0.28)"};
            display: inline-flex;
            align-items: center;
            gap: 8px;
            position: relative;
            overflow: hidden;
          }
          .promo-btn::before {
            content: "";
            position: absolute;
            top: 0;
            left: -100%;
            width: 100%;
            height: 100%;
            background: linear-gradient(90deg, transparent, rgba(255, 255, 255, 0.18), transparent);
            transition: left 0.6s ease;
          }
          .promo-btn:hover::before {
            left: 100%;
          }
          .promo-btn:hover {
            transform: translateY(-2px);
            box-shadow: 0 10px 25px ${isDark ? "rgba(102,126,234,0.5)" : "rgba(56,25,49,0.38)"};
          }
          .promo-btn:active {
            transform: translateY(1px);
          }
          .promo-btn-recreate {
            background: transparent;
            color: ${themeVars.primary};
            border: 1px solid ${themeVars.borderStrong};
            box-shadow: none;
          }
          .promo-btn-recreate:hover {
            background: ${themeVars.tagBg};
            box-shadow: 0 8px 18px ${themeVars.primary}22;
          }
          .promo-bottom {
            background: transparent;
            border: none;
            padding: 0;
            margin: ${isMobile ? "32px 0" : "60px 0"};
            display: flex;
            flex-direction: column;
            align-items: center;
            justify-content: center;
            gap: ${isMobile ? "16px" : "20px"};
            text-align: center;
          }
          .promo-bottom .promo-text {
            text-align: center;
            margin: 0;
          }
          .promo-bottom .promo-title {
            font-size: ${isMobile ? "1.4rem" : "1.8rem"};
            margin-bottom: ${isMobile ? "8px" : "10px"};
          }
          .promo-bottom .promo-subtitle {
            font-size: ${isMobile ? "0.9rem" : "1.1rem"};
            max-width: ${isMobile ? "100%" : "600px"};
          }
          .promo-bottom .glass-btn {
            background: transparent;
            border: 1px solid ${themeVars.borderStrong};
            border-radius: ${isMobile ? "8px" : "10px"};
            padding: ${isMobile ? "8px 20px" : "12px 28px"};
            color: ${themeVars.primary};
            font-size: ${isMobile ? "0.85rem" : "0.95rem"};
            font-weight: 600;
            cursor: pointer;
            transition: all 0.25s ease;
          }
          .promo-bottom .glass-btn-recreate {
            color: ${themeVars.primary};
          }
          .promo-bottom .glass-btn:hover {
            background: ${themeVars.tagBg};
            border-color: ${themeVars.primary};
            transform: translateY(-1px);
            box-shadow: 0 4px 16px rgba(56,25,49,0.14);
          }
          .promo-bottom .glass-btn:active {
            transform: scale(0.98);
          }
        `}</style>
      </div>
    </>
  );
}